import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Palette, 
  Layout, 
  Smartphone, 
  Eye, 
  Brush,
  Layers,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import i18n from '../../i18n';

const WebDesign = () => {
  const { t, ready } = useTranslation();

  // Force reload translations when component mounts
  useEffect(() => {
    console.log('WebDesign component mounted, reloading translations');
    i18n.reloadResources().then(() => {
      console.log('Translations successfully reloaded in WebDesign');
      // Debug translation values
      console.log('Current language:', i18n.language);
      console.log('Title translation:', t('services.webDesign.title'));
      console.log('Description translation:', t('services.webDesign.description'));
    }).catch(err => {
      console.error('Failed to reload translations in WebDesign:', err);
    });
  }, [t]);

  const features = [
    {
      icon: Layout,
      title: t('services.webDesign.features.userFriendly.title'),
      description: t('services.webDesign.features.userFriendly.description')
    },
    {
      icon: Smartphone,
      title: t('services.webDesign.features.responsive.title'),
      description: t('services.webDesign.features.responsive.description')
    },
    {
      icon: Eye,
      title: t('services.webDesign.features.visual.title'),
      description: t('services.webDesign.features.visual.description')
    },
    {
      icon: Brush,
      title: t('services.webDesign.features.custom.title'),
      description: t('services.webDesign.features.custom.description')
    },
    {
      icon: Layers,
      title: t('services.webDesign.features.consistent.title'),
      description: t('services.webDesign.features.consistent.description')
    }
  ];

  const designProcess = [
    {
      step: 1,
      title: t('services.webDesign.process.step1.title'),
      description: t('services.webDesign.process.step1.description')
    },
    {
      step: 2,
      title: t('services.webDesign.process.step2.title'),
      description: t('services.webDesign.process.step2.description')
    },
    {
      step: 3,
      title: t('services.webDesign.process.step3.title'),
      description: t('services.webDesign.process.step3.description')
    },
    {
      step: 4,
      title: t('services.webDesign.process.step4.title'),
      description: t('services.webDesign.process.step4.description')
    }
  ];

  // If translations are not ready, show loading state
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center">Hleður...</div>;
  }

  return (
    <>
      <SEO 
        title={t('services.webDesign.title')}
        description={t('services.webDesign.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('services.webDesign.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('services.webDesign.description')}
              </p>
              <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                  >
                    {t('common.getQuote')}
                    <ArrowRight className="ml-2" />
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Features Section */}
        <motion.div 
          className="py-24 bg-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('services.webDesign.featuresTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('services.webDesign.featuresDescription')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div 
                  key={index}
                  className="bg-gray-50 rounded-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                    <feature.icon className="w-6 h-6 text-[#EA295A]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div 
          className="py-24 bg-gray-50"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('services.webDesign.processTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('services.webDesign.processDescription')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div>
                <div className="space-y-8">
                  {designProcess.map((step) => (
                    <div key={step.step} className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#EA295A] text-white font-bold">
                          {step.step}
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div className="relative">
                <img 
                  src="https://res.cloudinary.com/djimcqajg/image/upload/v1741534746/designer-working-with-colour-wheel-and-digitizer-i-2023-11-27-05-16-14-utc_qv00tm.jpg"
                  alt="Web Design Process"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#EA295A]/10 to-transparent" />
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="py-24 bg-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
              <motion.h2 
                className="text-3xl font-bold text-white mb-6"
              >
                {t('services.webDesign.cta.title')}
              </motion.h2>
              <motion.p 
                className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
              >
                {t('services.webDesign.cta.description')}
              </motion.p>
              <motion.div>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                  >
                    {t('common.getQuote')}
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default WebDesign;